/**
 * Read the admin `accessToken` cookie. SSR-safe. Returns null if
 * absent. `apiFetch` reads this and forwards it as
 * `Authorization: Bearer <token>` because the cookie's parent
 * domain (`.getlost.me`) does not match the public API host
 * (`api.getlost.cloud`); the browser will not auto-forward the
 * cookie cross-origin.
 */
export function getAccessToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)accessToken=([^;]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Read the admin `refreshToken` cookie from `document.cookie`. Same
 * SSR-safe shape as `getAccessToken`. Returns null if absent.
 */
export function getRefreshToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)refreshToken=([^;]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Read the `refreshTokenIssuedAt` cookie, which the admin login flow
 * sets to the timestamp (ms since epoch) at issue time. Used to enforce
 * the 30-day max age on the refresh cookie (mirrors
 * getlost-admin/src/utils/api.ts:139-143).
 *
 * Returns null if absent or unparseable.
 */
function getRefreshTokenIssuedAt(): number | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)refreshTokenIssuedAt=([^;]+)/);
  if (!match) return null;
  const v = parseInt(decodeURIComponent(match[1]), 10);
  return Number.isFinite(v) ? v : null;
}

const REFRESH_TOKEN_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Set one or more cookies scoped to `.getlost.me` (with dev fallbacks).
 *
 * Mirrors `setCookiesForWildcardDomain` from
 * `getlost-admin/src/utils/api.ts:443-459`. Kept as a copy because the
 * web app does not depend on the admin app's runtime — copying the
 * small helper keeps the cross-repo surface narrow.
 */
function setCookiesForWildcardDomain(values: Record<string, string>): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;

  const host = window.location.hostname;
  const domainAttr =
    host === "getlost.me" || host.endsWith(".getlost.me")
      ? "; Domain=.getlost.me"
      : "";

  const isHttps = window.location.protocol === "https:";
  const secureAttr = isHttps ? "; Secure" : "";
  const sameSiteAttr = "; SameSite=Lax";
  const pathAttr = "; Path=/";

  Object.entries(values).forEach(([key, val]) => {
    const k = encodeURIComponent(key);
    const v = encodeURIComponent(val);
    document.cookie = `${k}=${v}${pathAttr}${domainAttr}${secureAttr}${sameSiteAttr}`;
  });
}

/**
 * Delete one cookie. Mirrors `deleteCookie` from
 * `getlost-admin/src/utils/api.ts:28-41`.
 */
function deleteCookie(name: string): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const host = window.location.hostname;
  const domainAttr =
    host === "getlost.me" || host.endsWith(".getlost.me")
      ? "; Domain=.getlost.me"
      : "";
  const pathAttr = "; Path=/";
  const expiresPast = "; Expires=Thu, 01 Jan 1970 00:00:00 GMT";

  const k = encodeURIComponent(name);
  document.cookie = `${k}=; Max-Age=0${pathAttr}${domainAttr}`;
  document.cookie = `${k}=; Max-Age=0${pathAttr}`;
  document.cookie = `${k}=; ${expiresPast}${pathAttr}${domainAttr}`;
  document.cookie = `${k}=; ${expiresPast}${pathAttr}`;
}

/**
 * Persist `expires_in` (seconds) into localStorage with a 60s buffer,
 * so a future preemptive-refresh check has headroom. Mirrors
 * `setAccessExpiryFromSeconds` from
 * `getlost-admin/src/utils/api.ts:118-126`.
 */
function setAccessExpiryFromSeconds(expiresIn?: number): void {
  if (typeof window === "undefined") return;
  if (typeof expiresIn !== "number") return;
  const bufferSec = 60;
  const ts = Date.now() + Math.max(0, expiresIn - bufferSec) * 1000;
  try {
    window.localStorage.setItem("accessTokenExpiresAt", String(ts));
  } catch {
    // localStorage may be unavailable (private mode, quota); ignore.
  }
}

interface ImportMetaEnvShape {
  VITE_API_BASE_URL?: string;
  VITE_API_PREFIX?: string;
}

const importMetaEnv: ImportMetaEnvShape =
  (import.meta as unknown as { env?: ImportMetaEnvShape }).env ?? {};

const API_BASE =
  importMetaEnv.VITE_API_BASE_URL ??
  `https://${importMetaEnv.VITE_API_PREFIX ?? "api"}.getlost.cloud`;

/**
 * Single-flight guard for the in-flight refresh. Multiple concurrent
 * 401s share one refresh call instead of triggering N parallel
 * `/admin/v1/refresh/` round-trips. Mirrors the `refreshing` module-level
 * promise from `getlost-admin/src/utils/api.ts:64`.
 */
let refreshing: Promise<boolean> | null = null;

/**
 * Exchange the refresh-token cookie for a fresh access+refresh pair.
 * Returns `true` on success (cookies + localStorage updated), `false`
 * on any non-2xx, parse failure, missing cookie, or refresh-cookie
 * older than 30 days. The backend at `POST /admin/v1/refresh/` is
 * defined in `monolith-typescript/src/lib/controllers/AdminAuth.controller.ts:185-343`.
 */
async function refreshAdminToken(): Promise<boolean> {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    const issuedAt = getRefreshTokenIssuedAt();
    if (issuedAt && Date.now() - issuedAt > REFRESH_TOKEN_MAX_AGE_MS) {
      return false;
    }

    const url = `${API_BASE}/admin/v1/refresh/`;
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!resp.ok) {
      return false;
    }

    const data = (await resp.json().catch(() => ({}))) as {
      access_token?: string;
      refresh_token?: string;
      expires_in?: number;
    };
    const newAccess = data.access_token;
    if (!newAccess) {
      return false;
    }

    // Treat the new refresh token as authoritative; the backend may
    // rotate. Same as `getlost-admin/src/utils/api.ts:174`.
    const refreshToUse = data.refresh_token || refreshToken;

    deleteCookie("accessToken");
    deleteCookie("refreshToken");
    deleteCookie("refreshTokenIssuedAt");
    setCookiesForWildcardDomain({
      accessToken: newAccess,
      refreshToken: refreshToUse,
      refreshTokenIssuedAt: data.refresh_token
        ? String(Date.now())
        : getRefreshTokenIssuedAt()?.toString() || String(Date.now()),
    });

    if (typeof data.expires_in === "number") {
      setAccessExpiryFromSeconds(data.expires_in);
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Fetch wrapper for the public-web API. Forwards the admin access
 * token as `Authorization: Bearer <token>` (re-derived on every
 * loop iteration from the cookie, since the cookie may have been
 * rotated by `refreshAdminToken`). Caller-supplied Authorization
 * headers win. On 401, refreshes the access+refresh cookies once
 * via `POST /admin/v1/refresh/` and replays; concurrent 401s share
 * one refresh.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  let hasRefreshed = false;
  while (true) {
    // Re-derive the Authorization header on every iteration so the
    // post-refresh cookie value is what the retry sends. Caller
    // Authorization headers always win.
    const headers = new Headers(init.headers);
    if (!headers.has("authorization")) {
      const token = getAccessToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
    }
    const resp = await fetch(path, { ...init, headers });

    if (resp.status !== 401 || hasRefreshed) return resp;
    hasRefreshed = true;

    if (!refreshing) {
      refreshing = refreshAdminToken().finally(() => {
        refreshing = null;
      });
    }
    await refreshing;
  }
}
